import { useMutation, useQuery } from "react-query";
import {
  getUserContentAndProgressTree,
  takeActionAgainstContent,
} from "../services/content.service";
import {
  ArticleContent,
  FormattedContent,
  IContentWithProgress,
  Question,
} from "../@types";
import { useContentAndProgress } from "../contexts/UserProgressContext";
import { useCallback, useEffect, useState } from "react";
import { submitAnswer } from "../services/selfAssessment.service";

export function useContentActions({
  content,
  questionId,
  callback,
}: {
  content: FormattedContent | Question;
  questionId?: string;
  callback?: any;
}) {
  const [contentProgress, setContentProgress] =
    useState<IContentWithProgress | null>(null);

  const { updateUserProgressTree, activeMilestoneContent } =
    useContentAndProgress();
  const [pendingAnswers, setPendingAnswers] = useState<{
    [key: string]: "Yes" | "Maybe" | "No";
  }>({});

  const { refetch: refreshUserProgressState } = useQuery(
    ["userContentProgress", content],
    () => getUserContentAndProgressTree({ platformContentId: content?.id }),
    {
      onSuccess: (data: IContentWithProgress) => {
        setContentProgress(data);
        updateUserProgressTree();
      },
    }
  );
  useEffect(() => {
    refreshUserProgressState();
  }, [activeMilestoneContent]);

  const contentAction = useMutation(takeActionAgainstContent, {
    onSuccess: () => refreshUserProgressState(),
  });

  const saveAnswers = useMutation({
    mutationFn: async ({
      answers,
    }: {
      answers: Record<string, string | null>;
    }) => {
      if (Object.keys(answers).length === 0) return;

      return submitAnswer({
        selfAssessmentId: content.id,
        answers: Object.entries(answers).map(([questionId, answer]) => ({
          questionId,
          answer:
            answer === null ? "" : (answer as "" | "Yes" | "Maybe" | "No"),
        })),
      });
    },
    onSuccess: () => {
      console.log("here.");
      setTimeout(() => {
        refreshUserProgressState();
      }, 500);
      callback();
      setPendingAnswers({});
    },
  });

  const answerQuestion = (answer: "Yes" | "Maybe" | "No") => {
    setPendingAnswers((prev) => ({
      ...prev,
      [questionId || ""]: answer,
    }));
  };

  const handleBookmark = useCallback(
    (e: any) => {
      e.stopPropagation();
      const articleId = contentProgress?.content?.id;
      if (!articleId) {
        return;
      }
      contentAction.mutate({ contentId: articleId, action: "Bookmark" });
    },
    [contentProgress]
  );

  const handleLike = useCallback(
    (e: any) => {
      e.stopPropagation();
      const articleId = contentProgress?.content?.id;
      if (!articleId) return;
      contentAction.mutate({ contentId: articleId, action: "Like" });
    },
    [contentProgress]
  );

  const handleDislike = useCallback(
    (e: any) => {
      e.stopPropagation();
      const articleId = contentProgress?.content?.id;
      if (!articleId) return;
      contentAction.mutate({ contentId: articleId, action: "Dislike" });
    },
    [contentProgress]
  );

  const handleDoneClick = useCallback(
    (e: any) => {
      e.stopPropagation();
      contentAction.mutate({
        contentId: contentProgress?.content?.id || contentProgress?.id || 0,
        action: "Finish",
      });
    },
    [contentProgress]
  );

  const handleRollbackDoneClick = useCallback(
    (e: any) => {
      e.stopPropagation();
      contentAction.mutate({
        contentId: contentProgress?.content?.id || contentProgress?.id || 0,
        action: "Unfinish",
      });
    },
    [contentProgress]
  );

  return {
    contentProgress,
    answerQuestion,
    saveAnswers,
    handleBookmark,
    handleDoneClick,
    handleRollbackDoneClick,
    handleLike,
    handleDislike,
  };
}
